import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  CloudArrowUpIcon,
  SparklesIcon,
  PlayIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  FlagIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import DocumentSidebar from '../components/DocumentSidebar';
// ProcessingStatusBar removed — processing state shown in Pending tab
import FileComparisonView from '../components/FileComparisonView';
import KeystoneSubmitResultsModal, { KeystoneSubmitRow } from '../components/KeystoneSubmitResultsModal';
import { setSubmitError, clearSubmitError, setSubmitSuccess } from '../utils/keystoneSubmitErrors';
import ErrorModal from '../components/ErrorModal';
import ConfirmModal from '../components/ConfirmModal';
import ExportModal from '../components/ExportModal';
import FeedbackWidget from '../components/FeedbackWidget';
import MultiInvoiceSplitter from '../components/MultiInvoiceSplitter';
import * as pdfjsLib from 'pdfjs-dist';
import { useFiles } from '../contexts/FilesContext';
import { useAuth } from '../contexts/AuthContext';
import { isFeatureEnabled } from '../config/accountSettings';
import { FileUploadState, InvoiceData, OCRFeedback } from '../types/invoice';
import { OCRApiService } from '../services/api';
import toast from 'react-hot-toast';

const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp', '.gif'];
const MAX_FILE_SIZE = 3 * 1024 * 1024;
const WARN_FILE_SIZE = 2 * 1024 * 1024;

// Point pdf.js at the CDN worker for the fast page-count peek we do before
// deciding whether to open the multi-invoice splitter modal. Rendering
// happens inside MultiInvoiceSplitter itself, this is just a metadata
// read on the main-thread accept.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

// Peek at a PDF's page count without rendering anything. Small allocation,
// used only to route multi-page PDFs through the splitter modal.
async function pdfPageCount(file: File): Promise<number> {
  try {
    const buf = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    return pdf.numPages;
  } catch {
    // If pdf.js can't parse it, treat as single-page and let the OCR
    // pipeline surface the real error downstream.
    return 1;
  }
}

/**
 * WorkspacePage — the single-page workspace that replaces Home, Dashboard, and Results.
 * Upload → Process → Review → Edit → Export, all without leaving the page.
 */
const WorkspacePage: React.FC = () => {
  const {
    files,
    setFiles,
    addFiles,
    invoices,
    loadOldInvoices,
    updateEditedData,
    updateFeedback,
    updateInvoice,
    selectedFileId,
    setSelectedFileId,
    activeJobIds,
    setActiveJobIds,
    checkedIds,
    setCheckedIds,
    sidebarTab,
    setSidebarTab,
    formDirtyRef,
    formSaveRef,
  } = useFiles();

  const { user } = useAuth();
  const isStateTaxEnabled = isFeatureEnabled(user?.account, 'state_tax_code');

  const [isProcessing, setIsProcessing] = useState(false);
  const [isExporting, setIsExporting] = useState<'csv' | 'txt' | null>(null);
  const [isSubmittingKeystone, setIsSubmittingKeystone] = useState(false);
  const [submitReport, setSubmitReport] = useState<{
    destination: string;
    results: KeystoneSubmitRow[];
  } | null>(null);
  const [exportErrors, setExportErrors] = useState<string[] | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isReportWrongOpen, setIsReportWrongOpen] = useState(false);
  // Tracks an export intent that's blocked on a pending save. Two shapes:
  //   { intent: 'open-modal' }    — user clicked Export; save, then re-enter the flow (validate + open modal)
  //   { intent: 'run', format }   — user picked format in modal; save, then run the export
  type PendingExport =
    | { intent: 'open-modal' }
    | { intent: 'run'; format: 'csv' | 'txt' };
  const [pendingExport, setPendingExport] = useState<PendingExport | null>(null);

  // Load invoices on mount
  useEffect(() => {
    loadOldInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map job ID → file ID so we know exactly which file to remove when a job completes
  const jobToFileMap = useRef<Map<number, string>>(new Map());
  const completedJobIds = useRef<Set<number>>(new Set());

  // Poll jobs while processing — move each file to Done individually as it completes
  useEffect(() => {
    if (activeJobIds.length === 0) {
      completedJobIds.current = new Set();
      return;
    }

    const poll = async () => {
      try {
        const data = await OCRApiService.getJobs();
        const tracked = data.jobs.filter((j: any) => activeJobIds.indexOf(j.id) !== -1);

        // Multi-invoice PDF split: any tracked parent that flipped to 'split'
        // has spawned N children we now need to follow to completion. Add
        // those child job IDs to the tracked set so polling continues until
        // each child is reviewed.
        const newlySplit = tracked.filter(
          (j: any) => j.status === 'split' && !completedJobIds.current.has(j.id)
        );
        if (newlySplit.length > 0) {
          const parentIds = new Set(newlySplit.map((j: any) => j.id));
          const children = data.jobs.filter(
            (j: any) => j.parent_job_id != null && parentIds.has(j.parent_job_id)
          );
          const childIds = children.map((c: any) => c.id);
          if (childIds.length > 0) {
            setActiveJobIds((prev) => Array.from(new Set([...prev, ...childIds])));
          }
          newlySplit.forEach((p: any) => {
            completedJobIds.current.add(p.id);
            toast.success(
              `Detected ${p.split_child_count ?? childIds.length} invoices in ${p.filename}. Processing each individually.`
            );
            // Replace the parent's Pending row with one Pending row per child
            // so the user sees "still working" state until each child's OCR
            // completes — instead of the parent silently vanishing before
            // the invoices land in Done.
            const parentFileId = jobToFileMap.current.get(p.id);
            const parentChildren = children.filter((c: any) => c.parent_job_id === p.id);
            setFiles(prev => {
              const withoutParent = parentFileId
                ? prev.filter(f => f.id !== parentFileId)
                : prev;
              const synthetic: FileUploadState[] = parentChildren.map((child: any) => {
                const syntheticFileId = `split-child-${child.id}`;
                jobToFileMap.current.set(child.id, syntheticFileId);
                return {
                  file: new File([], child.filename, { type: 'application/pdf' }),
                  id: syntheticFileId,
                  status: 'processing',
                  progress: 50,
                } as FileUploadState;
              });
              return [...withoutParent, ...synthetic];
            });
          });
        }

        // Find newly completed jobs (children flow through here on their own)
        const newlyDone = tracked.filter(
          (j: any) => (j.status === 'needs_review' || j.status === 'ready' || j.status === 'error')
            && !completedJobIds.current.has(j.id)
        );

        if (newlyDone.length > 0) {
          console.log('[QUEUE] Newly done jobs:', newlyDone.map((j: any) => `job=${j.id} file=${j.filename} status=${j.status}`));
          newlyDone.forEach((j: any) => completedJobIds.current.add(j.id));

          // Remove exact files by ID using the job→file mapping
          const fileIdsToRemove = new Set(
            newlyDone.map((j: any) => jobToFileMap.current.get(j.id)).filter(Boolean)
          );
          console.log('[QUEUE] File IDs to remove:', Array.from(fileIdsToRemove));
          console.log('[QUEUE] jobToFileMap:', Array.from(jobToFileMap.current.entries()));

          setFiles(prev => {
            console.log('[QUEUE] Files before remove:', prev.map(f => `${f.id}:${f.file.name}:${f.status}`));
            const after = prev.filter(f => !fileIdsToRemove.has(f.id));
            console.log('[QUEUE] Files after remove:', after.map(f => `${f.id}:${f.file.name}:${f.status}`));
            return after;
          });

          // Refresh invoices so newly completed ones appear in Done
          await loadOldInvoices();
        }

        // If all tracked jobs are done, clear activeJobIds to stop polling.
        // 'split' counts as "not done" until we've added its children to
        // the tracked set (handled above) — after that the parent is in
        // completedJobIds and the children take over the polling budget.
        const allDone = tracked.length > 0 && tracked.every(
          (j: any) => j.status !== 'queued' && j.status !== 'processing' && j.status !== 'split'
        );
        if (allDone) {
          setActiveJobIds([]);
        }
      } catch (e) {
        // Ignore polling errors
      }
    };

    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobIds]);

  // Find the selected file from invoices — memoize to prevent unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedFile = useMemo(() => selectedFileId ? invoices.find(f => f.id === selectedFileId) : null, [selectedFileId, invoices]);
  const completedFiles = useMemo(() => invoices.filter(f => f.status === 'completed' && f.result), [invoices]);
  // Everything reviewable: completed invoices plus any that bounced back
  // from a Keystone submit (status === 'error') so clerks can fix them and
  // resubmit without having to clear the error status by hand first.
  const submittableFiles = useMemo(
    () => invoices.filter(f => (f.status === 'completed' || f.status === 'error') && f.result),
    [invoices],
  );
  const pendingFiles = files.filter(f => f.status === 'pending');
  const hasPendingFiles = pendingFiles.length > 0;

  // ---- Processing ----

  const startProcessing = async () => {
    if (isProcessing || pendingFiles.length === 0) return;

    // Process only checked pending files, or all pending if none checked
    const filesToProcess = checkedIds.size > 0
      ? pendingFiles.filter(f => checkedIds.has(f.id))
      : pendingFiles;

    if (filesToProcess.length === 0) return;

    setIsProcessing(true);
    setCheckedIds(new Set());

    // Mark ALL selected files as processing immediately
    const processingIds = new Set(filesToProcess.map(f => f.id));
    setFiles(prev =>
      prev.map(f =>
        processingIds.has(f.id) ? { ...f, status: 'processing' as const, progress: 50 } : f
      )
    );

    toast.success(`Submitting ${filesToProcess.length} file(s)...`);

    const submittedIds: number[] = [];

    try {
      for (const fileState of filesToProcess) {
        try {
          const job = await OCRApiService.submitJob(fileState.file);
          submittedIds.push(job.id);
          jobToFileMap.current.set(job.id, fileState.id);
          setActiveJobIds([...submittedIds]);
        } catch (error) {
          setFiles(prev =>
            prev.map(f =>
              f.id === fileState.id
                ? { ...f, status: 'error' as const, progress: 0, error: 'Failed to submit' }
                : f
            )
          );
          toast.error(`Failed to submit ${fileState.file.name}`);
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // ---- Export ----

  // Files to export — only checked ones
  const filesToExport = submittableFiles.filter(f => checkedIds.has(f.id));

  // Mirrors backend validate_invoice_data — surfaces errors before the export modal opens
  const validateExportData = (invoices: InvoiceData[]): string[] => {
    const errors: string[] = [];
    for (const inv of invoices) {
      const invNum = inv.invoice_number || 'unknown';
      const poNumber = (inv.po_number || '').trim();
      const lineItems = inv.line_items || [];
      const hasTax = Number(inv.total_tax || 0) > 0;

      if (lineItems.length === 0) {
        if (!poNumber) {
          errors.push(`Invoice "${invNum}": Needs a PO Number or GL ID on each line item.`);
        }
        continue;
      }

      lineItems.forEach((li, i) => {
        const glId = (li.gl_id || '').trim();
        const taxCode = (li.tax_code || '').trim();
        const label = `Invoice "${invNum}", line ${i + 1}`;
        // Each line needs at least one of PO Number or GL ID. They are
        // not mutually exclusive, a line with both is fine (the GL
        // overrides or supplements the PO's default). Mirrors
        // export_routes.py validate_invoice_data.
        if (!poNumber && !glId) {
          errors.push(`${label}: Needs a PO Number or GL ID.`);
        } else if (glId.toUpperCase() === 'TBD') {
          errors.push(`${label}: GL ID is still TBD, enter the real account number.`);
        }

        if (isStateTaxEnabled && hasTax && !taxCode) {
          errors.push(`${label}: Invoice has sales tax, enter a Tax Code under Show More.`);
        }
      });
    }
    return errors;
  };

  const handleOpenExportModal = () => {
    if (filesToExport.length === 0) {
      toast.error('Select invoices in the sidebar to export');
      return;
    }
    // Unsaved changes: prompt save FIRST so validation and export both run against
    // the persisted state, not the edit buffer. After save, we re-enter this flow.
    if (formDirtyRef.current) {
      setPendingExport({ intent: 'open-modal' });
      return;
    }
    const invoiceDataList = filesToExport
      .map(f => f.editedData || f.result?.data)
      .filter((data): data is InvoiceData => data !== undefined);
    const errors = validateExportData(invoiceDataList);
    if (errors.length > 0) {
      setExportErrors(errors);
      return;
    }
    setIsExportModalOpen(true);
  };

  const runExport = async (format: 'csv' | 'txt') => {
    if (filesToExport.length === 0) {
      toast.error('Select invoices in the sidebar to export');
      return;
    }

    setIsExporting(format);
    try {
      const rawInvoiceDataList = filesToExport
        .map(file => file.editedData || file.result?.data)
        .filter((data): data is InvoiceData => data !== undefined);

      if (rawInvoiceDataList.length === 0) {
        throw new Error('No valid invoice data to export');
      }

      // Column U (Comp PO Y/N): if the user lacks the permission, force N.
      // If they have permission, use their default preference unless the
      // line already carries an explicit override (per-line checkbox).
      const canComplete = !!user?.can_complete_po_line;
      const defaultYes = canComplete && !!user?.default_complete_po_yes;
      const invoiceDataList = rawInvoiceDataList.map(inv => ({
        ...inv,
        line_items: (inv.line_items || []).map(li => ({
          ...li,
          complete_po_line: !canComplete
            ? 'N'
            : (li.complete_po_line ?? (defaultYes ? 'Y' : 'N')),
        })),
      }));

      const blob = format === 'txt'
        ? await OCRApiService.exportKeystoneTXT(invoiceDataList)
        : await OCRApiService.exportKeystoneCSV(invoiceDataList);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `keystone_import_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${invoiceDataList.length} invoice(s)`);
      setIsExportModalOpen(false);
    } catch (error: any) {
      console.error('Export failed:', error);
      // Close the format-picker modal before surfacing the error so the user
      // can actually see what's wrong (otherwise the error modal renders
      // behind the export picker).
      setIsExportModalOpen(false);
      if (error?.response?.status === 422) {
        const validationErrors = error.response?.data?.detail?.validation_errors;
        if (Array.isArray(validationErrors)) {
          const cleanErrors = validationErrors.map((e: string) =>
            e.replace(/Invoice '([^']*)',?\s*line \d+:\s*/, 'Invoice "$1": ')
             .replace('Neither PO Number nor GL ID is set. Provide one.', 'Needs a PO Number or GL ID.')
             .replace('Both PO Number and GL ID are set. Remove one.', 'Has both PO Number and GL ID — remove one.')
             .replace("GL ID is still 'TBD'. Enter a real account number.", 'GL ID is still TBD — enter the real account number.')
             .replace('No PO Number and no line items with GL ID.', 'Needs a PO Number or GL ID on each line item.')
          );
          setExportErrors(cleanErrors);
        } else {
          setExportErrors(['Could not export. Please check your invoice data and try again.']);
        }
      } else {
        toast.error('Export failed. Please try again.');
      }
    } finally {
      setIsExporting(null);
    }
  };

  const handleExportFromModal = async (format: 'csv' | 'txt') => {
    if (formDirtyRef.current) {
      setIsExportModalOpen(false);
      setPendingExport({ intent: 'run', format });
      return;
    }
    await runExport(format);
  };

  const runKeystoneSubmit = async () => {
    if (filesToExport.length === 0) {
      toast.error('Select invoices in the sidebar to submit');
      return;
    }

    // Build the aligned list: for each selected file, grab its invoice data.
    // Keep the file objects alongside so we can mark the exact file as
    // errored (and move it to the Errors tab) after we hear back from
    // Keystone. Order matters — the backend returns results in the same
    // order we send them, so index-aligned zip works.
    const selected = filesToExport
      .map(file => ({ file, data: file.editedData || file.result?.data }))
      .filter((row): row is { file: typeof filesToExport[number]; data: InvoiceData } => row.data !== undefined);
    if (selected.length === 0) {
      toast.error('No valid invoice data to submit');
      return;
    }

    // ---- Pre-flight validation ----
    // Catch rejections we can predict (missing vendor_number, line/header
    // total mismatch, etc.) before sending anything to Keystone. Each
    // blocked invoice gets marked as errored and shows up in the results
    // modal with a specific "here's what to fix" message, so the clerk
    // isn't waiting on a Keystone round-trip for issues we already know
    // about.
    const preflightFailures: {
      file: typeof selected[number]['file'];
      invoiceNumber: string;
      errors: string[];
    }[] = [];

    for (const { file, data } of selected) {
      const issues: string[] = [];

      if (!data.vendor?.vendor_number?.toString().trim()) {
        issues.push('Vendor is not matched to a Keystone vendor record (vendor_number is empty).');
      }
      if (!data.invoice_number?.toString().trim()) {
        issues.push('Invoice number is empty.');
      }

      // Keystone enforces strict equality between the invoice header total
      // and the sum of line_items. Match Keystone's math exactly so clerks
      // see the same mismatch we'd see on their side (not a frontend-only
      // calculation that disagrees with the server). Keystone's rules:
      //   * qty > 0  -> line total = qty * unit_price
      //   * qty <= 0 -> line total = unit_price   (flat charge: shipping,
      //                                             fees, services, etc.)
      // Our old formula multiplied by qty unconditionally, which zeroed out
      // every flat-charge line and let through invoices Keystone then
      // rejected with "detail total doesn't match the Invoice Total".
      const lines = data.line_items || [];
      if (lines.length > 0) {
        const lineSum = lines.reduce((acc, li: any) => {
          const q = Number(li?.quantity || 0);
          const up = Number(li?.unit_price || 0);
          const value = q > 0 ? q * up : up;
          return acc + (Number.isFinite(value) ? value : 0);
        }, 0);
        const headerTotal = Number(data.total_amount || 0);
        if (Math.abs(lineSum - headerTotal) > 0.005) {
          const diff = (headerTotal - lineSum).toFixed(2);
          issues.push(
            `Line items sum to ${lineSum.toFixed(2)} but the invoice total is ` +
            `${headerTotal.toFixed(2)} (off by ${diff}). Adjust a line's quantity or unit price, ` +
            `or change the invoice total so they match.`,
          );
        }
      }

      if (issues.length > 0) {
        preflightFailures.push({
          file,
          invoiceNumber: data.invoice_number || file.file.name,
          errors: issues,
        });
      }
    }

    if (preflightFailures.length > 0) {
      // Mark each blocked invoice as errored and surface the issues in the
      // standard results modal, same UX as a real Keystone rejection.
      // Also persist to localStorage so the error state survives a refresh.
      preflightFailures.forEach(({ file, errors }) => {
        const message = `Validation: ${errors.join(' • ')}`;
        updateInvoice(file.id, { status: 'error', error: message });
        setSubmitError(file.invoiceId, message);
      });

      const rows: KeystoneSubmitRow[] = preflightFailures.map(({ invoiceNumber, errors }) => ({
        invoice_number: invoiceNumber,
        success: false,
        step: 'validation',
        status_code: null,
        response: { errors },
      }));

      setSubmitReport({
        destination: 'Blocked by KeyScan validation — nothing was sent to Keystone.',
        results: rows,
      });
      setIsExportModalOpen(false);
      setSidebarTab('error');
      toast.error(
        preflightFailures.length === 1
          ? '1 invoice needs attention before it can be submitted.'
          : `${preflightFailures.length} invoices need attention before they can be submitted.`,
      );
      return;
    }

    const canComplete = !!user?.can_complete_po_line;
    const defaultYes = canComplete && !!user?.default_complete_po_yes;
    const invoiceDataList = selected.map(({ file, data: inv }) => ({
      ...inv,
      // Inject the DB invoice id from the file state so the backend can
      // look up the matching ProcessingJob and attach the original PDF
      // in step 2. The InvoiceData payload itself doesn't always carry
      // `id` (loadOldInvoices omits it; fresh OCR results don't have
      // one yet), so without this the backend's _load_pdf_bytes gets
      // None, step 2 is silently skipped, and the invoice posts to
      // Keystone without its PDF. Observed on 2026-10-07 against prod.
      id: file.invoiceId ?? inv.id,
      line_items: (inv.line_items || []).map(li => ({
        ...li,
        complete_po_line: !canComplete
          ? 'N'
          : (li.complete_po_line ?? (defaultYes ? 'Y' : 'N')),
      })),
    }));

    setIsSubmittingKeystone(true);
    const pendingToast = toast.loading(
      invoiceDataList.length === 1 ? 'Submitting to Keystone…' : `Submitting ${invoiceDataList.length} invoices…`,
    );
    try {
      const report = await OCRApiService.submitToKeystone(invoiceDataList);
      toast.dismiss(pendingToast);

      // Walk the results and reflect each outcome back onto the matching
      // file in the sidebar. Failures become errors (move to Errors tab,
      // clerk sees the Keystone reason). Successes clear any prior error
      // AND stamp a submittedAt / result_key so the sidebar can show a
      // green "Submitted" badge instead of just lumping them into Done
      // alongside not-yet-submitted invoices. Both halves are persisted
      // to localStorage so a browser refresh keeps everything in place.
      const selectedWasErrored = selectedFile?.status === 'error';
      const anySuccess = report.results.some(r => r.success);
      report.results.forEach((row, idx) => {
        const file = selected[idx]?.file;
        if (!file) return;
        if (row.success) {
          const stamp = new Date().toISOString();
          updateInvoice(file.id, {
            status: 'completed',
            error: undefined,
            submittedAt: stamp,
            submitResultKey: row.result_key,
            submitPdfAttached: row.pdf_attached,
          });
          clearSubmitError(file.invoiceId);
          setSubmitSuccess(file.invoiceId, {
            submittedAt: stamp,
            resultKey: row.result_key,
            pdfAttached: row.pdf_attached,
          });
        } else {
          const errors = extractKeystoneErrors(row.response);
          const summary = errors.length
            ? errors.join(' • ')
            : `Keystone rejected (${row.step || 'unknown'} step, HTTP ${row.status_code ?? '—'})`;
          const message = `Keystone: ${summary}`;
          updateInvoice(file.id, { status: 'error', error: message });
          setSubmitError(file.invoiceId, message);
        }
      });

      // UX: when a submit cleared the error status for the invoice the clerk
      // was looking at, they'd otherwise be stranded on the Errors tab with
      // a now-stale-looking page. Jump them to Done and drop the selection
      // so they can see the submitted invoice land in the right tab.
      if (anySuccess && selectedWasErrored && sidebarTab === 'error') {
        setSidebarTab('completed');
        setSelectedFileId(null);
        setCheckedIds(new Set());
      }

      const okCount = report.results.filter(r => r.success).length;
      const failCount = report.results.length - okCount;

      if (report.success) {
        toast.success(
          okCount === 1
            ? `Submitted to Keystone ✓  key: ${report.results[0]?.result_key || '—'}`
            : `All ${okCount} invoices submitted to Keystone ✓`,
          { duration: 6000 },
        );
        setIsExportModalOpen(false);
      } else {
        // Jump the sidebar to Errors so the clerk sees the rejected invoices
        // land in the right tab. Open the results modal so they can read the
        // specific Keystone reason without hunting for it.
        if (failCount > 0) setSidebarTab('error');
        setSubmitReport({ destination: report.destination, results: report.results });
        setIsExportModalOpen(false);
        if (okCount > 0) {
          toast.success(`${okCount} submitted ✓`, { duration: 4000 });
        }
      }
    } catch (error: any) {
      toast.dismiss(pendingToast);
      console.error('Submit to Keystone failed', error);
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Submit failed: ${detail}`);
    } finally {
      setIsSubmittingKeystone(false);
    }
  };

  /** Pull a flat error-string list out of whatever shape Keystone returned
   *  (JSON `errors` array, string `error`, FastAPI `detail`, or raw text). */
  const extractKeystoneErrors = (response: unknown): string[] => {
    if (!response || typeof response !== 'object') return [];
    const r = response as Record<string, unknown>;
    const out: string[] = [];
    if (Array.isArray(r.errors)) for (const e of r.errors) out.push(String(e));
    if (typeof r.error === 'string') out.push(r.error);
    if (typeof r.detail === 'string') out.push(r.detail);
    if (typeof r._raw === 'string') out.push(r._raw);
    return out;
  };

  const handleSubmitFromModal = async () => {
    if (formDirtyRef.current) {
      toast.error('You have unsaved changes. Save first, then submit.');
      return;
    }
    await runKeystoneSubmit();
  };

  const handleSaveAndContinueExport = async () => {
    const pending = pendingExport;
    if (!pending) return;
    setPendingExport(null);
    if (formSaveRef.current) {
      try {
        await formSaveRef.current();
      } catch {
        toast.error('Failed to save changes. Export cancelled.');
        return;
      }
    }
    if (pending.intent === 'open-modal') {
      // Re-enter the flow: now that the form is clean, validate against persisted values
      handleOpenExportModal();
    } else {
      await runExport(pending.format);
    }
  };

  // ---- Data update handler (memoized to prevent re-renders) ----

  const handleDataUpdate = useCallback((updatedData: InvoiceData) => {
    if (!selectedFileId) return;
    const original = invoices.find(f => f.id === selectedFileId)?.result?.data;
    const merged: InvoiceData = {
      ...updatedData,
      file_url: updatedData.file_url || original?.file_url,
    };
    updateEditedData(selectedFileId, merged);
  }, [selectedFileId, invoices, updateEditedData]);

  const handleFeedbackSubmit = useCallback(async (feedback: OCRFeedback) => {
    if (selectedFileId && selectedFile) {
      try {
        await OCRApiService.submitFeedback(selectedFileId, selectedFile.file.name, feedback);
        updateFeedback(selectedFileId, feedback);
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        throw error;
      }
    }
  }, [selectedFileId, selectedFile, updateFeedback]);

  // ---- Drop zone for empty state ----

  // Multi-page PDFs are pulled aside and shown to the user in the splitter
  // modal so they can decide whether each is one invoice or several before
  // anything hits the OCR queue. Everything else (single-page PDFs, images)
  // flows through the existing addFiles path untouched.
  const [splitterFiles, setSplitterFiles] = useState<File[] | null>(null);

  const enqueueFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;
    const newFiles: FileUploadState[] = files.map((file) => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      status: 'pending' as const,
      progress: 0,
    }));
    addFiles(newFiles);
    setSidebarTab('pending');
  }, [addFiles, setSidebarTab]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const validFiles = acceptedFiles.filter((file) => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`${file.name}: unsupported file type`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: file too large (max 3MB).`);
        return false;
      }
      if (file.size > WARN_FILE_SIZE) {
        toast(`${file.name}: large file (${(file.size / (1024 * 1024)).toFixed(1)}MB) — may use more processing credits`, { icon: '⚠️' });
      }
      return true;
    });
    if (validFiles.length === 0) return;

    // Route multi-page PDFs through the splitter modal, and let everything
    // else (single-page PDFs, images) hit the queue immediately.
    const straightThrough: File[] = [];
    const multiPage: File[] = [];
    for (const file of validFiles) {
      if (file.type === 'application/pdf') {
        const pages = await pdfPageCount(file);
        if (pages > 1) {
          multiPage.push(file);
          continue;
        }
      }
      straightThrough.push(file);
    }
    if (straightThrough.length > 0) enqueueFiles(straightThrough);
    if (multiPage.length > 0) {
      setSplitterFiles((prev) => (prev ? [...prev, ...multiPage] : multiPage));
    }
  }, [enqueueFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp', '.gif'],
    },
    multiple: true,
    noClick: false,
  });

  // ---- Render ----

  const isEmpty = files.length === 0 && invoices.length === 0 && activeJobIds.length === 0;
  // Errored invoices (Keystone rejected on submit) also render the edit view
  // so the clerk can fix the data and resubmit. Only hide when the file
  // isn't a reviewable invoice yet.
  const showComparisonView = !!selectedFile
    && (selectedFile.status === 'completed' || selectedFile.status === 'error')
    && selectedFile.result;
  const selectedFileError = selectedFile?.status === 'error' ? selectedFile.error : null;

  return (
    <div className="flex h-[calc(100vh-4.5rem)] overflow-hidden">
      {/* Document Sidebar */}
      <div className="w-[280px] flex-shrink-0">
        <DocumentSidebar onProcessFiles={startProcessing} />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Processing status removed from center — processing state shows in Pending tab */}

          {/* Export / Submit toolbar — shows on Done and Errors tabs so
              clerks can resubmit rejected invoices after fixing them. */}
          {submittableFiles.length > 0 && (sidebarTab === 'completed' || sidebarTab === 'error') && (
            <div className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 mb-4">
              <div className="flex items-center gap-2">
                <DocumentTextIcon className={`h-5 w-5 ${sidebarTab === 'error' ? 'text-red-500' : 'text-green-500'}`} />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {checkedIds.size > 0
                    ? `${filesToExport.length} invoice${filesToExport.length !== 1 ? 's' : ''} selected`
                    : sidebarTab === 'error'
                      ? 'Fix the invoice below, select it, then press Export to resubmit'
                      : 'Select invoices to export or submit'
                  }
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleOpenExportModal}
                  disabled={isExporting !== null || isSubmittingKeystone || checkedIds.size === 0}
                  className="flex items-center justify-center gap-1.5 px-6 py-2 min-w-[140px] bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {isExporting !== null ? 'Exporting…' : isSubmittingKeystone ? 'Submitting…' : 'Export'}
                </button>
              </div>
            </div>
          )}

          {/* Process button removed from center — moved to sidebar Pending tab */}

          {/* Report Wrong — hidden for now per user request (2026-04-22). Keep the
              button + modal wired up so we can re-enable by flipping this flag. */}
          {false && showComparisonView && (
            <div className="flex justify-center mb-4">
              <button
                onClick={() => setIsReportWrongOpen(true)}
                title="Flag this extraction as incorrect so it can be used as AI training data"
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-400 hover:bg-amber-500 text-gray-900 rounded-lg text-sm font-semibold transition-colors"
              >
                <FlagIcon className="h-4 w-4" />
                Report Wrong
              </button>
            </div>
          )}

          {/* Rejection / validation banner — shows above the edit view when
              this invoice came back from a failed submit (either Keystone
              rejected it, or KeyScan blocked it before send). The clerk
              sees the specific reason without digging through logs. */}
          {selectedFileError && (selectedFileError.startsWith('Keystone:') || selectedFileError.startsWith('Validation:')) && (
            <div className="mb-4 flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                  {selectedFileError.startsWith('Validation:')
                    ? 'This invoice is not ready to submit'
                    : 'Keystone rejected this invoice'}
                </p>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1 break-words">
                  {selectedFileError.replace(/^(Keystone|Validation):\s*/, '')}
                </p>
                <p className="text-xs text-red-600/80 dark:text-red-400/80 mt-2">
                  Fix the fields below, save, then select this invoice and press Submit again.
                </p>
              </div>
            </div>
          )}

          {/* Main content: comparison view or empty state */}
          {showComparisonView ? (
            <FileComparisonView
              key={selectedFileId}
              file={selectedFile.file}
              data={(selectedFile.editedData || selectedFile.result!.data)!}
              filename={selectedFile.file.name}
              invoiceId={selectedFile.invoiceId}
              previewUrl={selectedFile.previewUrl}
              feedback={selectedFile.feedback}
              onFeedbackSubmit={handleFeedbackSubmit}
              onDataUpdate={handleDataUpdate}
              compact
              fileSize={selectedFile.fileSize}
              dirtyRef={formDirtyRef}
              saveRef={formSaveRef}
            />
          ) : (
            /* Drop zone + message — always visible when not editing */
            <div className="space-y-6">
              <div
                {...getRootProps()}
                className={`flex flex-col items-center justify-center py-16 rounded-2xl border-2 border-dashed transition-all duration-300 cursor-pointer ${
                  isDragActive
                    ? 'border-brand-400 bg-brand-50/50 dark:bg-brand-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-brand-400 hover:bg-brand-50/30 dark:hover:bg-brand-900/10'
                }`}
              >
                <input {...getInputProps()} />
                <div className="p-4 bg-brand-600 rounded-xl mb-4">
                  <SparklesIcon className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-1">
                  {isDragActive ? 'Drop files here' : 'Upload Invoices'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Drag and drop PDF or image files, or click to browse
                </p>
              </div>

              {completedFiles.length > 0 && sidebarTab === 'completed' && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <DocumentTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="text-base font-medium text-gray-500 dark:text-gray-400">
                    Select a document from the sidebar to review
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Validation error modal */}
      {exportErrors && (
        <ErrorModal
          title="Cannot Export"
          errors={exportErrors}
          onClose={() => setExportErrors(null)}
        />
      )}

      {/* Multi-invoice PDF splitter. Opens when any dropped PDF has more
          than one page. When it completes, the resulting File(s) flow into
          the regular enqueueFiles path as if they were normal uploads. */}
      {splitterFiles && splitterFiles.length > 0 && (
        <MultiInvoiceSplitter
          files={splitterFiles}
          onComplete={(resultFiles) => {
            setSplitterFiles(null);
            enqueueFiles(resultFiles);
          }}
          onCancel={() => setSplitterFiles(null)}
        />
      )}

      {/* Export modal — batch due date override + format choice.
          Submit-to-Keystone is only wired up for SSO users (ones who
          arrived via KeyNet). Legacy break-glass + password users don't
          see the button because their tenant's write-back endpoint isn't
          configured yet; letting them click it would POST to whichever
          dev endpoint KEYSTONE_WRITE_URL happens to point at (not their
          actual Keystone DB) and silently create junk records on the
          wrong system. SSO users are identified by the `keynet:` prefix
          on their username (set in upsert_keynet_user). */}
      <ExportModal
        isOpen={isExportModalOpen}
        invoiceCount={filesToExport.length}
        isExporting={isExporting}
        isSubmitting={isSubmittingKeystone}
        onExport={handleExportFromModal}
        onSubmit={user?.username?.startsWith('keynet:') ? handleSubmitFromModal : undefined}
        onCancel={() => setIsExportModalOpen(false)}
      />

      {/* Keystone submit results — opens on any failure, lists per-invoice
          success/failure with the actual Keystone reason + a suggested fix.
          Clicking "Open this invoice" jumps straight to the edit view so
          the clerk can fix the rejected field. */}
      {submitReport && (
        <KeystoneSubmitResultsModal
          isOpen={true}
          destination={submitReport.destination}
          results={submitReport.results}
          onViewInvoice={(invoiceNumber) => {
            const match = invoices.find(f =>
              (f.editedData?.invoice_number || f.result?.data?.invoice_number) === invoiceNumber
            );
            if (match) {
              setSelectedFileId(match.id);
              setSidebarTab('error');
              setSubmitReport(null);
            }
          }}
          onClose={() => setSubmitReport(null)}
        />
      )}

      {/* Unsaved changes warning on export */}
      {pendingExport && (
        <ConfirmModal
          title="Unsaved Changes"
          message="You have unsaved changes. Save first, then continue to export?"
          confirmLabel="Save and Continue"
          cancelLabel="Cancel"
          destructive
          onConfirm={handleSaveAndContinueExport}
          onCancel={() => setPendingExport(null)}
        />
      )}

      {/* Report Wrong modal — FeedbackWidget pre-set to 'bad' so clerks flag bad
          extractions in one click. Routes through the existing /feedback pipeline. */}
      {isReportWrongOpen && selectedFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsReportWrongOpen(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-5">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FlagIcon className="h-5 w-5 text-amber-500" />
                Report Incorrect Extraction
              </h3>
              <button
                onClick={() => setIsReportWrongOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <FeedbackWidget
                filename={selectedFile.file.name}
                initialFeedback={{ rating: 'bad' }}
                onFeedbackSubmit={async (feedback) => {
                  await handleFeedbackSubmit(feedback);
                  setIsReportWrongOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspacePage;
